import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BannerData } from '@/types/banner';

interface BannerPopupProps {
  isOpen: boolean;
  onClose: () => void;
  data: BannerData | null;
  onCopy: (clipText: string, bannerId: string, creativeId?: string) => void;
  fullScreen?: boolean;
}

const BannerPopup: React.FC<BannerPopupProps> = ({
  isOpen,
  onClose,
  data,
  onCopy,
  fullScreen = true,
}) => {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  const handleCopy = useCallback(() => {
    if (!data || !data.clipText) return;

    navigator.clipboard.writeText(data.clipText)
      .then(() => {
        setCopied(true);
        onCopy(data.clipText!, data.id, data.creativeId);
        
        // 2秒後に元に戻す
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      })
      .catch((error) => {
        console.error('[BANNER-POPUP] Failed to copy text to clipboard:', error);
        const textArea = document.createElement('textarea');
        textArea.value = data.clipText!;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          setCopied(true);
          onCopy(data.clipText!, data.id, data.creativeId);
          setTimeout(() => {
            setCopied(false);
          }, 2000);
        } catch (err) {
          console.error('[BANNER-POPUP] Fallback copy also failed:', err);
        }
        document.body.removeChild(textArea);
      });
  }, [data, onCopy]);

  // descriptionの改行を処理（JSON文字列の\nを実際の改行に変換）
  const formatDescription = (description: string | undefined): string => {
    if (!description) return '';
    // JSON文字列の\nを実際の改行に変換
    return description.replace(/\\n/g, '\n');
  };

  if (!isOpen || !data) {
    return null;
  }

  return (
    <div
      className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[1000] tw-pointer-events-auto"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="banner-popup-title"
    >
      <div
        className="tw-bg-white tw-rounded-lg tw-p-6 tw-max-w-md tw-w-[90%] tw-max-h-[80vh] tw-overflow-y-auto tw-relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 閉じるボタン */}
        <button
          onClick={onClose}
          className="tw-absolute tw-top-4 tw-right-4 tw-text-gray-500 tw-text-2xl tw-leading-none tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-border-none tw-cursor-pointer tw-rounded-full tw-bg-transparent"
          aria-label="閉じる"
        >
          ×
        </button>

        {/* クーポンコード */}
        <div className="tw-mb-4">
          <h2 id="banner-popup-title" className="tw-text-lg tw-font-bold tw-mb-2">
            {t('banner.couponCode')}
          </h2>
          <div className="tw-bg-gray-100 tw-p-4 tw-rounded tw-text-center tw-font-mono tw-text-xl tw-font-bold">
            {data.clipText}
          </div>
        </div>

        {/* 説明文 */}
        {data.description && (
          <div className="tw-mb-4">
            <div
              className={`tw-text-sm tw-text-gray-700 tw-whitespace-pre-line tw-text-center ${
                !fullScreen ? 'tw-max-h-[200px] tw-overflow-y-auto' : ''
              }`}
            >
              {formatDescription(data.description)}
            </div>
          </div>
        )}

        {/* コピーボタン */}
        <button
          onClick={handleCopy}
          className="tw-w-full tw-py-4 tw-px-4 tw-bg-emerald-400 tw-text-white tw-rounded tw-font-medium hover:tw-bg-emerald-500 tw-transition-colors tw-duration-200 tw-border-none tw-cursor-pointer"
          style={{
            backgroundColor: copied ? '#10b981' : undefined,
          }}
        >
          {copied ? t('banner.copied') : t('banner.copy')}
        </button>
      </div>
    </div>
  );
};

export default BannerPopup;

